<script lang="ts">
    /**
     * 「연금술사가 되었다」 — 1단계 화면(돌아가는 수준까지 · 화려한 연출은 2단계, 작업지시서 10절).
     *
     * 화면은 세로: 위 = 이번 턴 아이템 셋, 가운데 = 연금술 솥(3칸), 아래 = 보관함(8칸, 4×2) (작업지시서 1절).
     *
     * 🚨 판정은 서버가 한다. 여기서는 「무엇을 넣었나」 만 보내고, 결과 · 등급 · 점수 · 다음 턴 아이템을 받아 그린다.
     * 한 턴 = 요청 한 번 — 앞 턴 결과를 보관함에 넣을지 버릴지(settle)는 **다음 요청에 함께 싣는다**(GameService 머리 주석).
     *   그래서 결과를 고른 뒤의 보관함은 이 화면이 먼저 그려 두고(localInventory), 다음 요청의 보관함 번호도 그 기준이다.
     */
    import { onMount } from 'svelte';
    import { api, PopApiError, type Board, type Game, type Item, type Note, type TurnResult } from './lib/api';
    import { collection } from './lib/collection.svelte';
    import { shareResult } from './lib/shareCard';
    import { resume } from './lib/resume';
    import { GRADE_COLORS, GRADE_TEXT, potionColor } from './lib/look';
    import { sound } from './lib/sound.svelte';
    import { askGyroPermission, gyroTilt, tilt } from './lib/tilt';
    import Cauldron, { type Phase } from './Cauldron.svelte';
    import Helper, { type Mood } from './Helper.svelte';

    let { shareUrl }: { shareUrl: string } = $props();

    type Pick = { from: 'offer' | 'inventory'; index: number };
    type Settle = { action: 'keep' | 'discard'; replace: number | null };

    const BREW_MS = 800; // 불 때는 연출 — 서버 왕복을 이 뒤에 숨긴다(작업지시서 7절)

    let game = $state<Game | null>(null);
    let localInventory = $state<Item[]>([]);
    let pendingView = $state<Item | null>(null);
    let settle = $state<Settle | null>(null);
    let replaceMode = $state(false);
    let picks = $state<Pick[]>([]);
    let lastResult = $state<TurnResult | null>(null);
    let resultSeq = $state(0); // 결과가 날 때마다 하나씩 — 같은 반응이 이어져도 방울이 움직임을 다시 튼다
    // 결과는 팝업으로 띄운다(스위티 2026-11-04) — 닫히면 솥 옆 방울이는 처음(기다림)으로 돌아간다
    let resultOpen = $state(false);
    // 새로고침 뒤 이어 붙인 판에 고르지 않은 결과가 남아 있다 — 「지난 턴에 만든 것」 팝업을 띄운다
    let resumed = $state(false);
    let shareNote = $state<string | null>(null);
    let sharing = $state(false);
    // 펑 뒤에 보여 줄 짧은 힌트 — 조합표가 아니라 「넣은 것의 모양」(같은 것 둘 · 셋을 넣음)만 보고 고른다
    let boomHint = $state<string | null>(null);
    let busy = $state(false);
    let brewing = $state(false);

    // ── 연출(작업지시서 6절 · 2단계) — 답은 이미 와 있고, 보여 주는 순서만 늦춘다 ──
    //    보글보글(brewing) → 등급 빛이 먼저 샌다(glow) → 빛기둥 · 파티클(burst) 또는 검은 연기(boom) → 결과 팝업
    let phase = $state<Phase>('idle');
    let fxGrade = $state(0);
    let fxBeat = $state(0);
    let brewItems = $state<Item[]>([]); // 불 땐 재료 — 고른 칸은 답이 오면 비지만, 물약 색은 연출이 끝날 때까지 남긴다
    let screenFx = $state<'shake' | 'legend' | 'soot' | null>(null);
    let screenTimer: ReturnType<typeof setTimeout> | undefined;
    let error = $state<string | null>(null);
    let lost = $state(false);
    let newBest = $state(false);

    let openedNote = $state<Note | null>(null); // 방금 펼친 쪽지 — 팝업으로 보여 준다
    let claim = $state<{ token: string; item: Item; legend: boolean; first: boolean } | null>(null);
    let tag = $state('');
    let tagError = $state<string | null>(null);
    let claimedTag = $state<string | null>(null);

    let view = $state<'play' | 'board'>('play');
    let board = $state<Board | null>(null);
    let boardError = $state<string | null>(null);

    const potSlots = $derived(game?.slots.pot ?? 3);
    const inventorySlots = $derived(game?.slots.inventory ?? 8);
    const potItems = $derived(
        picks.map((p) => (p.from === 'offer' ? game?.offer[p.index] : localInventory[p.index])).filter((x): x is Item => !!x),
    );
    const canPick = $derived(!!game && !game.over && !pendingView && !busy && !claim);
    const canBrew = $derived(canPick && potItems.length >= 2 && potItems.length <= potSlots);

    // 지금 무엇을 하면 되는지 — 이 단계에 맞는 것만 반짝이고, 맨 위 안내 한 줄도 이것을 따른다(스위티 2026-11-04)
    type Step = 'pick' | 'brew' | 'settle' | 'replace';
    const step = $derived.by((): Step | null => {
        if (!game || game.over || claim || busy) return null;
        if (replaceMode) return 'replace';
        if (pendingView || resultOpen) return 'settle';
        return potItems.length >= 2 ? 'brew' : 'pick';
    });
    // 솥 옆 방울이 — 기다림 · 두근두근 · 보글보글만. 결과에 대한 반응은 결과 팝업 안의 방울이가 한다.
    const mood = $derived.by((): Mood => {
        if (phase === 'boom') return 'boom';
        if (brewing || phase === 'glow' || phase === 'burst') return 'brewing';
        return picks.length > 0 ? 'focus' : 'idle';
    });
    // 결과 팝업 속 방울이 — 큰 것(영웅 이상 · 두 등급 · 첫 발견 · 보물 · 이야기 속 물건)이면 폴짝폴짝
    const resultMood = $derived.by((): Mood => {
        if (!lastResult || lastResult.outcome === 'pass') return 'idle';
        if (lastResult.outcome === 'boom') return 'boom';
        return isBig(lastResult) ? 'cheer' : 'happy';
    });

    function isBig(result: TurnResult): boolean {
        if (result.outcome !== 'made' && result.outcome !== 'graded') return false;
        const { item, bumpSteps, discovery } = result;
        return item.grade >= 3 || bumpSteps === 2 || discovery.first || discovery.legend || item.group === 'treasure' || item.group === 'story';
    }
    const summaryMood = $derived<Mood>(newBest || (game?.best?.grade ?? 0) >= 3 ? 'cheer' : 'happy');

    const guide = $derived.by(() => {
        switch (step) {
            case 'pick':
                return potItems.length === 0 ? '아이템을 눌러서 솥에 넣어 보세요' : '하나 더 넣어 보세요. 두 개부터 불을 땔 수 있어요';
            case 'brew':
                return potItems.length < potSlots ? '이제 불을 때 보세요. 하나 더 넣어도 돼요' : '이제 불을 때 보세요!';
            case 'settle':
                return '만든 아이템을 보관함에 넣을까요, 버릴까요?';
            case 'replace':
                return '보관함이 꽉 찼어요. 비울 칸을 눌러 주세요';
            default:
                return null;
        }
    });

    // $effect 가 아니라 onMount — boot() 가 상태를 읽고 쓰므로 $effect 면 스스로를 다시 부른다(무한 루프)
    onMount(() => {
        boot();
    });

    // 이어 하기 — 이 브라우저가 기억한 판이 서버에 남아 있으면 거기에 붙고, 없으면 새 판(Windows 방울이 prod 지적 2026-11-04)
    async function boot() {
        const savedClaim = resume.claim();
        if (savedClaim) openClaim(savedClaim);

        const id = resume.gameId();
        if (id) {
            busy = true;
            try {
                const res = await api.resume(id);
                if (!res.game.over) {
                    applyGame(res.game);
                    if (res.game.pending && !restoreSettle(res.game)) {
                        resumed = true;
                        resultOpen = true;
                    }
                    return;
                }
            } catch {
                // 사라진 판 — 아래에서 새 판을 연다
            } finally {
                busy = false;
            }
        }
        resume.saveGame(null);
        await start();
    }

    function openClaim(saved: { token: string; item: Item; legend: boolean; first: boolean }) {
        claim = { token: saved.token, item: saved.item, legend: saved.legend, first: saved.first };
        tag = '';
        tagError = null;
    }

    function applyGame(next: Game) {
        game = next;
        localInventory = [...next.inventory];
        pendingView = next.pending;
        settle = null;
        replaceMode = false;
        picks = [];
    }

    async function start() {
        busy = true;
        error = null;
        lost = false;
        lastResult = null;
        newBest = false;
        claimedTag = null;
        try {
            const res = await api.start();
            applyGame(res.game);
            resume.saveGame(res.game.id);
            resume.saveSettle(null);
        } catch (e) {
            error = e instanceof PopApiError ? e.message : '게임을 열지 못했어요';
        } finally {
            busy = false;
        }
    }

    function isPicked(from: Pick['from'], index: number) {
        return picks.some((p) => p.from === from && p.index === index);
    }

    // 레시피 쪽지 — 솥에 넣지 않고 펼친다. 턴은 넘어가지 않고, 그 칸은 비며, 조합의 반쪽이 보인다(작업지시서 5절)
    async function readNote(index: number) {
        if (!game || !canPick) return;
        busy = true;
        error = null;
        try {
            const res = await api.note(game.id, index);
            // 보관함 · 고른 칸 · 앞 턴 결과는 이 화면이 들고 있는 그대로 두고, 이번 턴 칸과 쪽지만 받는다
            game = { ...game, offer: res.game.offer, notes: res.game.notes };
            openedNote = res.note;
            sound.pick();
        } catch (e) {
            error = e instanceof PopApiError ? e.message : '쪽지를 펼치지 못했어요';
        } finally {
            busy = false;
        }
    }

    function noteLine(note: Note): string {
        return `${note.shown.emoji} ${note.shown.name}${' + ❓'.repeat(note.hidden)} → ${note.result.emoji} ${note.result.name}`;
    }

    function togglePick(from: Pick['from'], index: number) {
        if (from === 'offer' && game?.offer[index]?.note) {
            readNote(index);
            return;
        }
        if (from === 'offer' && !game?.offer[index]) return;
        if (replaceMode && from === 'inventory') {
            keepInto(index);
            return;
        }
        if (!canPick) return;
        if (isPicked(from, index)) {
            picks = picks.filter((p) => !(p.from === from && p.index === index));
        } else if (picks.length < potSlots) {
            picks = [...picks, { from, index }];
            sound.pick();
        }
    }

    // ── 앞 턴 결과: 보관함에 넣을지 버릴지 ──────────────────────────────
    function keep() {
        resultOpen = false;
        resumed = false;
        phase = 'idle';
        if (!pendingView) return;
        if (localInventory.length < inventorySlots) {
            localInventory = [...localInventory, pendingView];
            settle = { action: 'keep', replace: null };
            pendingView = null;
            rememberSettle();
        } else {
            replaceMode = true; // 가득 찼으면 팝업을 닫고 보관함에서 비울 칸을 고른다
        }
    }

    function keepInto(index: number) {
        if (!pendingView) return;
        const next = [...localInventory];
        next[index] = pendingView;
        localInventory = next;
        settle = { action: 'keep', replace: index };
        pendingView = null;
        replaceMode = false;
        rememberSettle();
    }

    // 고른 것은 다음 턴 요청에 실려 간다 — 그 전에 새로고침해도 또 묻지 않게 판 번호 · 턴과 함께 기억한다
    function rememberSettle() {
        if (game && settle) resume.saveSettle({ game: game.id, turn: game.turn, action: settle.action, replace: settle.replace });
    }

    // 이어 붙인 판에 기억해 둔 고름이 그대로 맞으면 다시 묻지 않고 적용한다. 안 맞으면 false(팝업으로 다시 묻는다)
    function restoreSettle(next: Game): boolean {
        const saved = resume.settle();
        if (!saved || saved.game !== next.id || saved.turn !== next.turn || !pendingView) return false;
        if (saved.action === 'keep') {
            if (saved.replace === null && localInventory.length < inventorySlots) {
                localInventory = [...localInventory, pendingView];
            } else if (saved.replace !== null && localInventory[saved.replace]) {
                const filled = [...localInventory];
                filled[saved.replace] = pendingView;
                localInventory = filled;
            } else {
                return false;
            }
        }
        settle = { action: saved.action, replace: saved.replace };
        pendingView = null;
        return true;
    }

    function closeResult() {
        resultOpen = false;
        phase = 'idle';
    }

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const calm = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function flashScreen(kind: 'shake' | 'legend' | 'soot', ms: number) {
        clearTimeout(screenTimer);
        screenFx = kind;
        screenTimer = setTimeout(() => (screenFx = null), ms);
    }

    // 결과를 보여 주기 전의 연출. 전설이면 빛이 더 오래 새고(잠깐 느려짐) 화면이 흔들리며 금빛이 터진다.
    async function reveal(result: TurnResult) {
        if (result.outcome === 'pass') return;
        const quiet = calm();
        if (result.outcome === 'boom') {
            phase = 'boom';
            fxBeat++;
            sound.boom();
            if (!quiet) flashScreen('soot', 900);
            await sleep(quiet ? 150 : 700);
            return;
        }
        const grade = result.item.grade;
        fxGrade = grade;
        phase = 'glow';
        await sleep(quiet ? 100 : grade >= 4 ? 1000 : grade >= 3 ? 600 : 350);
        phase = 'burst';
        fxBeat++;
        sound.chime(grade);
        if (!quiet && grade >= 4) flashScreen('legend', 1200);
        else if (!quiet && (grade >= 3 || result.bumpSteps === 2)) flashScreen('shake', 450);
        await sleep(quiet ? 100 : grade >= 4 ? 900 : 450);
    }

    function discard() {
        resultOpen = false;
        resumed = false;
        phase = 'idle';
        settle = { action: 'discard', replace: null };
        pendingView = null;
        replaceMode = false;
        rememberSettle();
    }

    // ── 한 턴 ─────────────────────────────────────────────────────────
    async function play(pass: boolean) {
        if (!game || busy || (!pass && !canBrew) || (pass && !canPick)) return;
        busy = true;
        brewing = !pass;
        if (!pass) {
            brewItems = [...potItems];
            phase = 'brewing';
            sound.bubble(BREW_MS);
        }
        error = null;
        claimedTag = null;
        const began = Date.now();
        const brewedKeys = pass ? [] : potItems.map((item) => item.key);
        const move = {
            game: game.id,
            settle: settle?.action ?? null,
            replace: settle?.replace ?? null,
            pass,
            offer: pass ? [] : picks.filter((p) => p.from === 'offer').map((p) => p.index),
            inventory: pass ? [] : picks.filter((p) => p.from === 'inventory').map((p) => p.index),
        };

        try {
            let res;
            for (let attempt = 0; ; attempt++) {
                try {
                    res = await api.turn(move);
                    break;
                } catch (e) {
                    // 턴 간격(0.6초)보다 빨랐다 — 조금 기다렸다 한 번 더
                    if (e instanceof PopApiError && (e.code === 'POP_TURN_TOO_FAST' || e.code === 'POP_TURN_BUSY') && attempt < 2) {
                        await new Promise((r) => setTimeout(r, 700));
                        continue;
                    }
                    throw e;
                }
            }
            const wait = BREW_MS - (Date.now() - began);
            if (!pass && wait > 0) await new Promise((r) => setTimeout(r, wait));
            brewing = false;
            await reveal(res.result);

            lastResult = res.result;
            resultSeq++;
            resultOpen = res.result.outcome !== 'pass';
            shareNote = null;
            boomHint = res.result.outcome === 'boom' && res.result.reason !== 'max_grade' ? hintFor(brewedKeys) : null;
            if (res.result.outcome === 'made' || res.result.outcome === 'graded') {
                collection.remember(res.result.item);
                const d = res.result.discovery;
                if (d.claim) {
                    const saved = { token: d.claim, item: res.result.item, legend: d.legend, first: d.first, at: Date.now() };
                    resume.saveClaim(saved); // 이름을 고르다 새로고침해도 다시 띄운다
                    openClaim(saved);
                }
            }
            applyGame(res.game);
            resume.saveSettle(null); // 고름은 이번 요청에 실려 갔다
            if (res.game.over) {
                newBest = collection.finish(res.game.score);
                resume.saveGame(null);
            }
        } catch (e) {
            if (e instanceof PopApiError && e.code === 'POP_GAME_NOT_FOUND') lost = true;
            error = e instanceof PopApiError ? e.message : '잠시 문제가 생겼어요';
            phase = 'idle';
        } finally {
            busy = false;
            brewing = false;
        }
    }

    // ── 첫 발견 3글자 ──────────────────────────────────────────────────
    async function submitTag() {
        if (!claim) return;
        tagError = null;
        try {
            const res = await api.claim(claim.token, tag);
            claimedTag = res.tag;
            claim = null;
            resume.saveClaim(null);
        } catch (e) {
            tagError = e instanceof PopApiError ? e.message : '이름을 남기지 못했어요';
        }
    }

    // 건너뛰면 이 자리를 다음 사람에게 넘긴다(스위티 결정 2026-11-04) — 받은 점수는 그대로
    function skipTag() {
        if (!claim) return;
        const token = claim.token;
        claim = null;
        resume.saveClaim(null);
        api.skip(token, game?.id ?? null).catch(() => {
            // 못 넘겨도 60분 뒤 서버가 저절로 연다
        });
    }

    // ── 이번 주 도감 ───────────────────────────────────────────────────
    async function openBoard() {
        view = 'board';
        boardError = null;
        try {
            board = await api.board();
        } catch (e) {
            boardError = e instanceof PopApiError ? e.message : '도감을 열지 못했어요';
        }
    }

    function resultText(result: TurnResult): string {
        if (result.outcome === 'boom') return result.reason === 'max_grade' ? '전설보다 높은 등급은 없어요. 펑!' : '펑! 솥이 터졌어요';
        if (result.outcome === 'pass') return '이번 턴은 넘겼어요';
        return result.outcome === 'graded' ? `${result.item.name} 등급이 올라갔어요` : `${withObject(result.item.name)} 만들었어요`;
    }

    // 규칙만 알려 주고 조합은 알려 주지 않는다 — 「이 둘은 안 된다 / 저 둘은 된다」 는 말하지 않는다
    function hintFor(keys: string[]): string {
        if (keys.length === 2 && keys[0] === keys[1]) {
            return '같은 아이템은 세 개를 넣어야 등급이 올라가요. 두 개로는 안 되는 것도 있어요.';
        }
        if (keys.length === 3) {
            return '두 개로 되는 조합에 하나를 더 넣어도 터져요. 하나를 빼거나 다른 걸 넣어 보세요.';
        }
        return '이 조합은 안 되나 봐요. 같은 아이템을 세 개 넣으면 등급이 올라가요.';
    }

    // 「물을」 · 「돌를」 — 받침이 있으면 을, 없으면 를(받침을 알 수 없는 글자는 를)
    function withObject(word: string): string {
        const code = word.charCodeAt(word.length - 1) - 0xac00;
        const final = code >= 0 && code <= 11171 ? code % 28 : 0;
        return word + (final > 0 ? '을' : '를');
    }

    // ── 자랑하기 ──────────────────────────────────────────────────────
    // 판 결과(끝난 판) 또는 방금 만든 아이템(결과 팝업)을 자랑한다
    async function brag(item: Item | null = null) {
        if (!game || sharing) return;
        sharing = true;
        shareNote = null;
        try {
            const shown = item ?? game.best;
            const best = shown ? { emoji: shown.emoji, name: shown.name, gradeLabel: shown.gradeLabel, color: GRADE_COLORS[shown.grade], textColor: GRADE_TEXT[shown.grade] } : null;
            let badge: string | null = null;
            if (item && lastResult && (lastResult.outcome === 'made' || lastResult.outcome === 'graded')) {
                const d = lastResult.discovery;
                badge = d.legend ? '이번 주 첫 전설!' : d.first ? '이번 주 첫 발견!' : item.group === 'story' ? '우리 이야기 속 물건!' : null;
            }
            const outcome = await shareResult({
                kind: item ? 'item' : 'game',
                score: game.score,
                best,
                badge,
                madeCount: game.madeCount,
                firsts: game.firsts,
                url: shareUrl,
            });
            if (outcome === 'saved') shareNote = '자랑 그림을 저장했어요. 자랑 글과 링크도 복사해 두었어요.';
        } catch {
            shareNote = '그림을 만들지 못했어요. 잠시 뒤에 다시 해 주세요.';
        } finally {
            sharing = false;
        }
    }

    function resetCountdown(endsAt: string): string {
        const ms = new Date(endsAt).getTime() - Date.now();
        if (ms <= 0) return '곧 새 주가 시작돼요';
        const days = Math.floor(ms / 86_400_000);
        const hours = Math.floor((ms % 86_400_000) / 3_600_000);
        return days > 0 ? `새 주까지 ${days}일 ${hours}시간 남았어요` : `새 주까지 ${hours}시간 남았어요`;
    }
</script>

{#snippet itemTile(item: Item, picked: boolean, onclick: () => void, label: string)}
    <button type="button" class="tile" class:picked style="--grade: {GRADE_COLORS[item.grade]}; --grade-text: {GRADE_TEXT[item.grade]}" {onclick} aria-pressed={picked} aria-label={label}>
        <span class="tile-emoji" aria-hidden="true">{item.emoji}</span>
        <span class="tile-name">{item.name}</span>
        <span class="tile-grade">{item.gradeLabel}</span>
    </button>
{/snippet}

<div class="alchemist" use:gyroTilt class:fx-shake={screenFx === 'shake' || screenFx === 'legend'}>
    {#if screenFx === 'legend' || screenFx === 'soot'}
        <div class="screen-fx {screenFx}" aria-hidden="true"></div>
    {/if}
    <nav class="tabs" aria-label="화면">
        <button type="button" class:active={view === 'play'} onclick={() => (view = 'play')}>솥</button>
        <button type="button" class:active={view === 'board'} onclick={openBoard}>이번 주 도감</button>
    </nav>

    {#if view === 'board'}
        <section class="board" aria-label="이번 주 도감">
            {#if boardError}
                <p class="error">{boardError}</p>
            {:else if !board}
                <p class="muted">도감을 펼치는 중…</p>
            {:else}
                <p class="muted">
                    첫 발견은 월요일 0시(한국 시각)에 모두 지워지고 새로 시작해요. <span class="nowrap">{resetCountdown(board.week.endsAt)}</span>
                </p>
                <p class="muted">내 도감 {collection.count} / {board.itemTotal} · 내 최고 점수 {collection.best.toLocaleString()}</p>

                {#if board.riddle}
                    <div class="riddle-board">
                        <h2>🧩 이번 주 보물 수수께끼</h2>
                        <p class="riddle-text">{board.riddle.text}</p>
                        {#if board.riddle.solved}
                            <p class="gold-line">
                                {board.riddle.solved.emoji} {board.riddle.solved.name} · 푼 사람 「{board.riddle.solved.tag ?? '이름 고르는 중'}」
                            </p>
                        {:else}
                            <p class="muted">아직 아무도 못 풀었어요. 첫 번째가 되어 보세요!</p>
                        {/if}
                    </div>
                {/if}

                {#if board.entries.length === 0}
                    <p class="empty">이번 주에는 아직 아무도 만들지 않았어요. 첫 발견자가 되어 보세요!</p>
                {:else}
                    <h2>첫 발견</h2>
                    <ul class="board-list">
                        {#each board.entries.filter((e) => e.kind === 'first') as entry (entry.key)}
                            <li>
                                <span class="board-emoji" aria-hidden="true">{entry.emoji}</span>
                                <span class="board-name">{entry.name}</span>
                                <span class="board-tag">첫 발견 {entry.tag ?? '이름 없음'}</span>
                                <span class="board-makers">{entry.makers}명이 만들었어요</span>
                            </li>
                        {/each}
                    </ul>
                    {#if board.entries.some((e) => e.kind === 'legend')}
                        <h2>첫 전설</h2>
                        <ul class="board-list legend">
                            {#each board.entries.filter((e) => e.kind === 'legend') as entry (entry.key)}
                                <li>
                                    <span class="board-emoji" aria-hidden="true">{entry.emoji}</span>
                                    <span class="board-name">{entry.name}</span>
                                    <span class="board-tag">첫 전설 {entry.tag ?? '이름 없음'}</span>
                                </li>
                            {/each}
                        </ul>
                    {/if}
                {/if}
            {/if}
        </section>
    {:else if !game}
        <section class="center">
            {#if error}
                <p class="error">{error}</p>
                <button type="button" class="primary" onclick={start}>다시 열기</button>
            {:else}
                <p class="muted">솥을 데우는 중…</p>
            {/if}
        </section>
    {:else}
        <header class="status">
            <span>턴 <strong>{game.turn}</strong> / {game.turns}</span>
            <span>점수 <strong>{game.score.toLocaleString()}</strong></span>
            <span class="flame" title="연속 성공 불꽃 — 터지지 않고 성공할수록 점수가 올라요">
                {game.flame > 0 ? '🔥'.repeat(game.flame) : '·'} ×{game.flameMultiplier}
            </span>
            <button type="button" class="sound-toggle" onclick={() => {
                    sound.toggle();
                    if (sound.on) askGyroPermission(); // 아이폰은 이 순간에만 기울기 허락을 물을 수 있다
                }} aria-pressed={sound.on} aria-label={sound.on ? '소리와 진동 끄기' : '소리와 진동 켜기'}>
                {sound.on ? '🔊' : '🔇'}
            </button>
        </header>

        {#if error}
            <p class="error" role="alert">
                {error}
                {#if lost}<button type="button" class="link" onclick={start}>새 판 시작</button>{/if}
            </p>
        {/if}

        {#if game.over}
            <section class="summary" aria-label="이번 판 결과">
                <h2>판이 끝났어요</h2>
                <p class="big">{game.score.toLocaleString()}점</p>
                <Helper mood={summaryMood} size="large" />
                {#if newBest}<p class="badge">내 최고 점수!</p>{/if}
                {#if game.best}
                    <p>이번 판 최고 아이템 <strong class="grade-text" style="--grade: {GRADE_COLORS[game.best.grade]}; --grade-text: {GRADE_TEXT[game.best.grade]}">{game.best.emoji} {game.best.name} ({game.best.gradeLabel})</strong></p>
                {/if}
                <p>만든 아이템 {game.madeCount}개 · 첫 발견 {game.firsts}개</p>
                <p class="muted">내 최고 점수 {collection.best.toLocaleString()} · 내 도감 {collection.count} / {game.itemTotal}</p>
                <div class="row">
                    <button type="button" class="primary brag" onclick={() => brag()} disabled={sharing}>{sharing ? '그리는 중…' : '자랑하기'}</button>
                    <button type="button" onclick={start} disabled={busy}>한 판 더</button>
                </div>
                {#if shareNote}<p class="muted" role="status">{shareNote}</p>{/if}
                <button type="button" class="link" onclick={openBoard}>이번 주 도감 보기</button>
            </section>
        {:else}
            <p class="guide" class:quiet={!guide} aria-live="polite">{guide ?? ' '}</p>
            {#if step === 'replace'}
                <div class="row">
                    <button type="button" onclick={discard}>넣지 않고 버리기</button>
                </div>
            {/if}

            <section aria-label="보관함">
                <h2 class="label">보관함 <span class="muted">{localInventory.length} / {inventorySlots}</span></h2>
                <div class="inventory" class:replacing={replaceMode} class:attract={step === 'pick'} class:full={step === 'brew' && picks.length >= potSlots} class:locked={step === 'settle' || busy}>
                    {#each Array(inventorySlots) as _, i (i)}
                        {#if localInventory[i]}
                            {@render itemTile(
                                localInventory[i],
                                isPicked('inventory', i),
                                () => togglePick('inventory', i),
                                replaceMode ? `${localInventory[i].name} 자리에 넣기` : `${localInventory[i].name} ${localInventory[i].gradeLabel} 솥에 넣기`,
                            )}
                        {:else}
                            <div class="tile empty-slot" aria-hidden="true"></div>
                        {/if}
                    {/each}
                </div>
            </section>

            <section aria-label="이번 턴 아이템">
                <h2 class="label">이번 턴 아이템 <span class="muted">고르지 않은 것은 턴이 끝나면 사라져요</span></h2>
                <div class="offer" class:attract={step === 'pick'} class:full={step === 'brew' && picks.length >= potSlots} class:locked={step === 'settle' || step === 'replace' || busy}>
                    {#each game.offer as item, i (i)}
                        {#if !item}
                            <div class="tile empty-slot read-note" aria-hidden="true"><span class="muted small">펼친 쪽지</span></div>
                        {:else if item.note}
                            <button type="button" class="tile note-tile" onclick={() => readNote(i)} disabled={!canPick} aria-label="찢어진 레시피 쪽지 펼치기">
                                <span class="tile-emoji" aria-hidden="true">🧾</span>
                                <span class="tile-name">찢어진 쪽지</span>
                                <span class="tile-grade">눌러서 펼치기</span>
                            </button>
                        {:else}
                            {@render itemTile(item, isPicked('offer', i), () => togglePick('offer', i), `${item.name} ${item.gradeLabel} 솥에 넣기`)}
                        {/if}
                    {/each}
                </div>
            </section>

            <section class="pot-wrap" aria-label="연금술 솥">
                <div class="pot-stage">
                    <div class="pot">
                        {#each Array(potSlots) as _, i (i)}
                            <div
                                class="pot-slot"
                                class:next={step === 'pick' && i === potItems.length}
                                style={potItems[i] ? `--grade: ${GRADE_COLORS[potItems[i].grade]}` : ''}
                            >
                                {#if potItems[i]}<span aria-hidden="true">{potItems[i].emoji}</span><span class="sr-only">{potItems[i].name}</span>{/if}
                            </div>
                        {/each}
                        <Cauldron color={potionColor(phase === 'idle' ? potItems : brewItems)} {phase} grade={fxGrade} beat={fxBeat} />
                    </div>
                    <Helper {mood} beat={resultSeq} />
                </div>
                <div class="row">
                    <button type="button" class="primary" class:cta={step === 'brew'} onclick={() => play(false)} disabled={!canBrew}>
                        {brewing ? '보글보글…' : '불 때기'}
                    </button>
                    <button type="button" onclick={() => play(true)} disabled={!canPick}>넘기기</button>
                </div>
                <p class="muted hint">두 개 이상 넣어야 불을 땔 수 있어요. 같은 아이템을 세 개 넣으면 등급이 올라가요.</p>
            </section>

            {#if game.notes.length > 0}
                <section class="notes" aria-label="펼친 쪽지">
                    <h2 class="label">펼친 쪽지 <span class="muted">{game.notes.length}장</span></h2>
                    <ul>
                        {#each game.notes as note, i (i)}
                            <li>{noteLine(note)}</li>
                        {/each}
                    </ul>
                </section>
            {/if}

            {#if game.riddle}
                <details class="riddle">
                    <summary>🧩 이번 주 보물 수수께끼</summary>
                    <p>{game.riddle}</p>
                    <p class="muted small">이 보물을 이번 주에 맨 처음 만든 사람이 수수께끼를 푼 사람이에요.</p>
                </details>
            {/if}
        {/if}
    {/if}

    {#if resultOpen && resumed && game && pendingView}
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="resumed-title">
            <div class="modal-card result-card" use:tilt>
                <Helper mood="happy" size="large" />
                <p class="result-emoji" style="--grade: {GRADE_COLORS[pendingView.grade]}" aria-hidden="true">{pendingView.emoji}</p>
                <h2 id="resumed-title">지난 턴에 만든 {pendingView.name}</h2>
                <p>
                    <strong class="grade-text" style="--grade: {GRADE_COLORS[pendingView.grade]}; --grade-text: {GRADE_TEXT[pendingView.grade]}">{pendingView.gradeLabel}</strong>
                    · 보관함에 넣을까요, 버릴까요?
                </p>
                <div class="row cta-row">
                    <button type="button" class="primary" onclick={keep}>보관함에 넣기</button>
                    <button type="button" onclick={discard}>버리기</button>
                </div>
            </div>
        </div>
    {/if}

    {#if resultOpen && game && lastResult && lastResult.outcome !== 'pass'}
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="result-title">
            <div class="modal-card result-card" class:boom={lastResult.outcome === 'boom'} use:tilt>
                <Helper mood={resultMood} beat={resultSeq} size="large" />
                {#if lastResult.outcome === 'boom'}
                    <p class="result-emoji" aria-hidden="true">💥</p>
                    <h2 id="result-title">{resultText(lastResult)}</h2>
                    {#if boomHint}<p class="boom-hint">💡 {boomHint}</p>{/if}
                    <div class="row">
                        <button type="button" class="primary" onclick={closeResult}>{game.over ? '결과 보기' : '확인'}</button>
                    </div>
                {:else}
                    {@const made = lastResult.item}
                    <p class="result-emoji" style="--grade: {GRADE_COLORS[made.grade]}" aria-hidden="true">{made.emoji}</p>
                    <h2 id="result-title">{resultText(lastResult)}</h2>
                    <p>
                        <strong class="grade-text" style="--grade: {GRADE_COLORS[made.grade]}; --grade-text: {GRADE_TEXT[made.grade]}">{made.gradeLabel}</strong> · +{lastResult.points.toLocaleString()}점
                    </p>
                    <p class="tags">
                        {#if lastResult.bumpSteps === 2}<span class="gold">불꽃이 가장 세서 두 등급 더!</span>{:else if lastResult.bumped}<span>운 좋게 한 등급 더!</span>{/if}
                        {#if lastResult.firstInGame}<span>이번 판에서 처음!</span>{/if}
                        {#if lastResult.discovery.first}<span class="gold">이번 주 첫 발견!</span>{/if}
                        {#if lastResult.discovery.legend}<span class="gold">첫 전설!</span>{/if}
                        {#if made.group === 'story'}<span class="gold">우리 이야기 속 물건!</span>{/if}
                        {#if claimedTag}<span>「{claimedTag}」 이름을 남겼어요</span>{/if}
                    </p>
                    {#if made.story}
                        <a class="story-link" href={made.story.url} target="_blank" rel="noopener">
                            📖 이 물건이 나오는 이야기 「{made.story.title}」 보러 가기
                        </a>
                    {/if}

                    {#if pendingView && !game.over}
                        <div class="row cta-row">
                            <button type="button" class="primary" onclick={keep}>보관함에 넣기</button>
                            <button type="button" onclick={discard}>버리기</button>
                        </div>
                    {:else}
                        <div class="row">
                            <button type="button" class="primary" onclick={closeResult}>{game.over ? '결과 보기' : '확인'}</button>
                        </div>
                    {/if}
                    <button type="button" class="link brag-link" onclick={() => brag(made)} disabled={sharing}>
                        📣 {sharing ? '그리는 중…' : '자랑하기'}
                    </button>
                    {#if shareNote}<p class="muted small" role="status">{shareNote}</p>{/if}
                {/if}
            </div>
        </div>
    {/if}

    {#if openedNote}
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="note-title">
            <div class="modal-card note-card" use:tilt>
                <p class="stamp note-stamp">레시피 쪽지</p>
                <h2 id="note-title">찢어진 쪽지를 펼쳤어요</h2>
                <p class="note-line">{noteLine(openedNote)}</p>
                <p class="muted small">❓ 자리의 재료는 찢겨 나갔어요. 나머지는 직접 찾아보세요. 펼친 쪽지는 이 판 동안 솥 아래에 모여요.</p>
                <div class="row">
                    <button type="button" class="primary" onclick={() => (openedNote = null)}>알겠어요</button>
                </div>
            </div>
        </div>
    {/if}

    {#if claim}
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="claim-title">
            <div class="modal-card">
                <p class="stamp">{claim.first ? '첫 발견' : '첫 전설'}</p>
                <h2 id="claim-title">{claim.item.emoji} {claim.item.name}</h2>
                <p>
                    이번 주에 {claim.first ? '이 아이템을' : '이 아이템을 전설 등급으로'} 맨 처음 만들었어요! 도감에 남길 이름을 세 글자까지 적어 주세요.
                </p>
                <input
                    type="text"
                    maxlength="3"
                    autocomplete="off"
                    aria-label="남길 이름 세 글자"
                    value={tag}
                    oninput={(e) => (tag = (e.currentTarget as HTMLInputElement).value)}
                />
                <p class="muted small">한글, 영문, 숫자로 세 글자까지 쓸 수 있어요. 실명이나 연락처는 적지 마세요.</p>
                <p class="muted small">건너뛰면 이 자리는 다음에 만드는 사람에게 넘어가요. 받은 점수는 그대로예요.</p>
                {#if tagError}<p class="error">{tagError}</p>{/if}
                <div class="row">
                    <button type="button" class="primary" onclick={submitTag} disabled={tag.trim().length === 0}>남기기</button>
                    <button type="button" onclick={skipTag}>건너뛰기</button>
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
    .alchemist {
        --pop: #c53a6b;
        max-width: 28rem;
        margin: 0 auto;
        padding: 0.75rem 1rem 2rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .tabs {
        display: flex;
        gap: 0.5rem;
    }

    .tabs button {
        flex: 1;
        padding: 0.55rem;
        border-radius: 999px;
        border: 1px solid rgba(197, 58, 107, 0.25);
        background: transparent;
        font-weight: 600;
        color: #6b7280;
    }

    .tabs button.active {
        background: var(--pop);
        border-color: var(--pop);
        color: #fff;
    }

    .status {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.95rem;
    }

    .label {
        font-size: 0.9rem;
        font-weight: 700;
        margin: 0 0 0.5rem;
    }

    .muted {
        color: #6b7280;
        font-weight: 400;
        font-size: 0.85rem;
    }

    .small {
        font-size: 0.78rem;
    }

    .offer {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.6rem;
    }

    .inventory {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 0.5rem;
    }

    .inventory.replacing .tile:not(.empty-slot) {
        outline: 2px dashed var(--pop);
    }

    .tile {
        --grade: #d1d5db;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.15rem;
        min-height: 5rem;
        padding: 0.5rem 0.25rem;
        border-radius: 14px;
        border: 2px solid var(--grade);
        background: #fff;
        box-shadow: 0 0 0 0 transparent;
        transition:
            transform 0.15s ease,
            box-shadow 0.15s ease;
    }

    .tile.picked {
        transform: translateY(-3px);
        box-shadow: 0 0 0 3px var(--pop);
    }

    .tile:not(.empty-slot):active {
        transform: scale(0.96);
    }

    /* ── 지금 할 일만 반짝인다 — step 하나가 정하고, 할 수 없는 것은 흐리게 ─────────── */
    .guide {
        margin: 0;
        padding: 0.55rem 0.9rem;
        border-radius: 999px;
        background: rgba(197, 58, 107, 0.1);
        color: var(--pop);
        font-weight: 700;
        font-size: 0.92rem;
        text-align: center;
    }

    .guide.quiet {
        visibility: hidden; /* 높이는 남겨 화면이 들썩이지 않게 */
    }

    .attract .tile:not(.picked):not(.empty-slot),
    .inventory.replacing .tile:not(.empty-slot) {
        animation: beckon 1.6s ease-in-out infinite;
    }

    .attract .tile:nth-child(2) {
        animation-delay: 0.2s;
    }

    .attract .tile:nth-child(3) {
        animation-delay: 0.4s;
    }

    .locked .tile:not(.empty-slot),
    .full .tile:not(.picked):not(.empty-slot) {
        opacity: 0.45;
        filter: saturate(0.6);
    }

    .pot-slot.next {
        border-color: var(--pop);
        animation: slot-wait 1.2s ease-in-out infinite;
    }

    .row button.cta,
    .row.cta-row button {
        animation: cta 1.3s ease-in-out infinite;
    }

    .row.cta-row button:not(.primary) {
        animation-delay: 0.65s;
    }

    @keyframes beckon {
        0%,
        100% {
            transform: translateY(0);
            box-shadow: 0 0 0 0 rgba(197, 58, 107, 0);
        }
        50% {
            transform: translateY(-2px);
            box-shadow: 0 0 0 4px rgba(197, 58, 107, 0.28);
        }
    }

    @keyframes slot-wait {
        0%,
        100% {
            background: rgba(255, 255, 255, 0.7);
            transform: scale(1);
        }
        50% {
            background: rgba(197, 58, 107, 0.16);
            transform: scale(1.06);
        }
    }

    @keyframes cta {
        0%,
        100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(197, 58, 107, 0.45);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 8px rgba(197, 58, 107, 0);
        }
    }

    /* 움직임을 줄여 달라는 기기 설정이면 흔들지 않고 테두리만 둔다 */
    @media (prefers-reduced-motion: reduce) {
        .attract .tile:not(.picked):not(.empty-slot),
        .inventory.replacing .tile:not(.empty-slot),
        .pot-slot.next,
        .row button.cta,
        .row.cta-row button {
            animation: none;
            box-shadow: 0 0 0 3px rgba(197, 58, 107, 0.3);
        }
    }

    .boom-hint {
        margin: 0.6rem auto 0;
        max-width: 22rem;
        padding: 0.5rem 0.8rem;
        border-radius: 0.75rem;
        background: rgba(245, 158, 11, 0.12);
        color: #92400e;
        font-size: 0.85rem;
        line-height: 1.5;
        word-break: keep-all;
    }

    .empty-slot {
        border-style: dashed;
        background: transparent;
        min-height: 4.2rem;
    }

    .tile-emoji {
        font-size: 1.8rem;
        line-height: 1;
    }

    .tile-name {
        font-size: 0.78rem;
        font-weight: 600;
        word-break: keep-all;
        text-align: center;
    }

    .tile-grade {
        font-size: 0.66rem;
        color: var(--grade-text, #4b5563);
        font-weight: 700;
    }

    .pot-stage {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        gap: 0.5rem;
    }

    /* 레시피 쪽지 · 수수께끼(3단계) */
    .note-tile {
        --grade: #d6b98c;
        background: repeating-linear-gradient(-8deg, #fffbeb, #fffbeb 10px, #fef3c7 10px, #fef3c7 11px);
        border-style: dashed;
        transform: rotate(-2deg);
    }

    .note-tile .tile-grade {
        color: #92400e;
    }

    .read-note {
        min-height: 5rem;
    }

    .note-stamp {
        border-color: #b45309;
        color: #b45309;
    }

    .note-line {
        font-size: 1.05rem;
        font-weight: 700;
        word-break: keep-all;
    }

    .notes ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
    }

    .notes li {
        padding: 0.4rem 0.7rem;
        border-radius: 0.6rem;
        background: #fffbeb;
        border: 1px dashed #d6b98c;
        font-size: 0.85rem;
        word-break: keep-all;
    }

    .riddle {
        padding: 0.6rem 0.9rem;
        border-radius: 0.9rem;
        background: rgba(245, 158, 11, 0.1);
        border: 1px solid rgba(245, 158, 11, 0.35);
    }

    .riddle summary {
        cursor: pointer;
        font-weight: 700;
        color: #92400e;
    }

    .riddle p {
        margin: 0.4rem 0 0;
        word-break: keep-all;
    }

    .riddle-board {
        margin: 0.5rem 0 1rem;
        padding: 0.8rem 1rem;
        border-radius: 1rem;
        background: rgba(245, 158, 11, 0.1);
        border: 1px solid rgba(245, 158, 11, 0.35);
    }

    .riddle-board h2 {
        margin: 0 0 0.4rem;
    }

    .riddle-text {
        font-weight: 600;
        word-break: keep-all;
    }

    .gold-line {
        color: #b45309;
        font-weight: 700;
    }

    :global(html.dark) .notes li,
    :global(html.dark) .note-tile {
        background: #3a2f1c;
        color: #f3f4f6;
    }

    .story-link {
        display: inline-block;
        margin-top: 0.25rem;
        padding: 0.55rem 0.9rem;
        border-radius: 0.8rem;
        background: rgba(245, 158, 11, 0.12);
        color: #92400e;
        font-size: 0.85rem;
        font-weight: 700;
        text-decoration: none;
        word-break: keep-all;
    }

    .summary :global(.helper) {
        align-self: center;
    }

    .pot-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
    }

    .pot {
        position: relative;
        display: flex;
        gap: 0.5rem;
        padding: 1rem 1rem 4.2rem;
        border-radius: 1.5rem;
        background: radial-gradient(circle at 50% 80%, rgba(197, 58, 107, 0.18), rgba(197, 58, 107, 0.04) 70%);
    }

    .pot-slot {
        --grade: #e5e7eb;
        position: relative;
        z-index: 1; /* 솥의 빛 · 빛기둥보다 위 */
        width: 3.6rem;
        height: 3.6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.9rem;
        border-radius: 50%;
        border: 2px dashed var(--grade);
        background: rgba(255, 255, 255, 0.7);
    }

    /* 폰 기울기(--gx · --gy · gyroTilt) — 판이 그쪽으로 살짝 기운다 */
    .offer,
    .inventory,
    .pot-stage {
        transform: perspective(900px) rotateX(calc(var(--gy, 0) * 1deg)) rotateY(calc(var(--gx, 0) * 1deg));
        transition: transform 0.15s ease-out;
    }

    .sound-toggle {
        border: none;
        background: none;
        font-size: 1.15rem;
        padding: 0.1rem 0.2rem;
    }

    /* 화면 연출 — 흔들림(영웅 이상 · 두 등급) · 금빛(전설) · 그을림(펑) */
    .fx-shake {
        animation: shake 0.45s ease-in-out;
    }

    .screen-fx {
        position: fixed;
        inset: 0;
        z-index: 150;
        pointer-events: none;
    }

    .screen-fx.legend {
        background: radial-gradient(circle at 50% 55%, rgba(253, 230, 138, 0.85), rgba(245, 158, 11, 0.35) 40%, transparent 75%);
        animation: fade-out 1.2s ease-out forwards;
    }

    .screen-fx.soot {
        background: radial-gradient(circle at 50% 55%, transparent 20%, rgba(17, 24, 39, 0.55) 80%);
        animation: fade-out 0.9s ease-out forwards;
    }

    @keyframes shake {
        0%,
        100% {
            transform: translate(0, 0);
        }
        20% {
            transform: translate(-4px, 2px);
        }
        40% {
            transform: translate(4px, -2px);
        }
        60% {
            transform: translate(-3px, -1px);
        }
        80% {
            transform: translate(3px, 1px);
        }
    }

    @keyframes fade-out {
        from {
            opacity: 1;
        }
        to {
            opacity: 0;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .fx-shake,
        .screen-fx {
            animation: none;
        }
    }

    .row {
        display: flex;
        gap: 0.5rem;
        justify-content: center;
    }

    button {
        cursor: pointer;
        font: inherit;
    }

    button:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    .row button {
        padding: 0.7rem 1.4rem;
        border-radius: 999px;
        border: 1px solid rgba(197, 58, 107, 0.35);
        background: #fff;
        font-weight: 600;
        color: var(--pop);
    }

    .row button.primary,
    .center .primary {
        background: var(--pop);
        border-color: var(--pop);
        color: #fff;
    }

    .hint {
        text-align: center;
        margin: 0;
    }

    /* 결과 팝업 — 이름 남기기 팝업과 같은 틀(.modal-card) */
    .result-card {
        animation: card-in 0.22s ease-out;
    }

    .result-card.boom {
        background: #f3f4f6;
    }

    .result-card h2 {
        font-size: 1.1rem;
        word-break: keep-all;
    }

    .result-card p {
        margin: 0;
    }

    .modal-card :global(.helper) {
        align-self: center;
    }

    .brag-link {
        align-self: center;
        padding: 0.3rem 0.6rem;
        font-weight: 700;
        font-size: 0.9rem;
        text-decoration: none;
    }

    @keyframes card-in {
        from {
            opacity: 0;
            transform: translateY(12px) scale(0.96);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .result-card {
            animation: none;
        }
    }

    .result-emoji {
        --grade: #d1d5db;
        font-size: 3rem;
        margin: 0;
        filter: drop-shadow(0 0 12px var(--grade));
    }

    .tags {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.35rem;
        margin: 0.5rem 0;
    }

    .tags span {
        font-size: 0.75rem;
        padding: 0.15rem 0.6rem;
        border-radius: 999px;
        background: rgba(197, 58, 107, 0.1);
        color: var(--pop);
        font-weight: 600;
    }

    .tags .gold {
        background: rgba(245, 158, 11, 0.15);
        color: #b45309;
    }

    .summary {
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    .big {
        font-size: 2.4rem;
        font-weight: 800;
        margin: 0;
        color: var(--pop);
    }

    .badge {
        align-self: center;
        padding: 0.2rem 0.8rem;
        border-radius: 999px;
        background: rgba(245, 158, 11, 0.15);
        color: #b45309;
        font-weight: 700;
        margin: 0;
    }

    .error {
        color: #b91c1c;
        font-size: 0.9rem;
    }

    .link {
        border: none;
        background: none;
        color: var(--pop);
        text-decoration: underline;
    }

    .center {
        text-align: center;
        padding: 3rem 0;
    }

    .board h2 {
        font-size: 1rem;
        margin: 1rem 0 0.5rem;
    }

    .board-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    .board-list li {
        display: grid;
        grid-template-columns: 2rem 1fr auto;
        grid-template-areas: 'emoji name tag' 'emoji makers makers';
        align-items: center;
        column-gap: 0.5rem;
        padding: 0.55rem 0.75rem;
        border-radius: 12px;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.06);
    }

    .board-list.legend li {
        border-color: rgba(245, 158, 11, 0.5);
    }

    .board-emoji {
        grid-area: emoji;
        font-size: 1.5rem;
    }

    .board-name {
        grid-area: name;
        font-weight: 700;
    }

    .board-tag {
        grid-area: tag;
        font-size: 0.8rem;
        color: #b45309;
        font-weight: 700;
    }

    .board-makers {
        grid-area: makers;
        font-size: 0.75rem;
        color: #6b7280;
    }

    .empty {
        text-align: center;
        padding: 2rem 0;
        color: #6b7280;
    }

    .modal {
        position: fixed;
        inset: 0;
        z-index: 200;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        background: rgba(17, 24, 39, 0.55);
    }

    .modal-card {
        width: 100%;
        max-width: 22rem;
        padding: 1.5rem;
        border-radius: 1.25rem;
        background: #fff;
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
    }

    .modal-card h2 {
        margin: 0;
    }

    .stamp {
        align-self: center;
        margin: 0;
        padding: 0.3rem 1rem;
        border: 3px solid #d97706;
        border-radius: 0.5rem;
        color: #d97706;
        font-weight: 800;
        transform: rotate(-6deg);
    }

    .modal-card input {
        text-align: center;
        font-size: 1.6rem;
        letter-spacing: 0.4em;
        padding: 0.5rem;
        border-radius: 0.75rem;
        border: 2px solid rgba(197, 58, 107, 0.35);
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    .grade-text {
        color: var(--grade-text);
    }

    .nowrap {
        white-space: nowrap;
    }

    .board p {
        word-break: keep-all;
    }

    /* 어두운 화면에서는 글자도 밝은 등급 색으로 */
    :global(html.dark) .grade-text,
    :global(html.dark) .tile-grade {
        color: var(--grade);
    }

    :global(html.dark) .tile,
    :global(html.dark) .board-list li,
    :global(html.dark) .modal-card,
    :global(html.dark) .row button:not(.primary) {
        background: #1f2937;
        color: #f3f4f6;
    }
</style>
